import React, { useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Briefcase, Lock, Mail, ShieldCheck, Wrench, Eye, EyeOff } from 'lucide-react';
import Login from '../Login';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDetail(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || '').filter(Boolean).join(' ') || 'Invalid input';
  return String(detail);
}

export default function PortalGate({ onCustomerLogin, onEmployeeLogin, onAdminLogin }) {
  const [mode, setMode] = useState('customer'); // customer | employee | admin
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const submitCustomer = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/spares/auth/login`, { email, password });
      localStorage.setItem('spare_token', data.token);
      localStorage.setItem('spare_user', JSON.stringify(data.customer));
      onCustomerLogin(data.customer);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  const submitAdmin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
      if (data.user?.role !== 'super_admin') {
        setError('Admin access only. This entrance is restricted to system administrators.');
        setLoading(false);
        return;
      }
      localStorage.setItem('spare_admin_token', data.token);
      localStorage.setItem('spare_admin_user', JSON.stringify(data.user));
      onAdminLogin(data.user);
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  if (mode === 'employee') {
    return (
      <div className="relative">
        <button onClick={() => setMode('customer')}
          className="fixed top-4 left-4 z-50 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 text-slate-300 text-xs font-semibold hover:bg-white/20 backdrop-blur border border-white/10 transition-colors"
          data-testid="back-to-portal-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Spare Parts Portal
        </button>
        <Login onLogin={onEmployeeLogin} />
      </div>
    );
  }

  const isAdmin = mode === 'admin';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #0b1524 0%, #0e2036 55%, #113052 100%)' }} data-testid="portal-gate">
      {/* Top bar */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-4">
        <div className="flex items-center gap-3">
          <img src="/images/kiel-logo.png" alt="Kiel Americas" className="h-9 object-contain" />
          <span className="hidden sm:block text-slate-400 text-xs tracking-widest uppercase font-semibold">Kiel Americas</span>
        </div>
        <button onClick={() => { setMode('employee'); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 text-slate-200 text-xs font-semibold hover:bg-white/15 transition-colors"
          data-testid="employee-login-btn">
          <Briefcase className="w-3.5 h-3.5" /> Employee Login
          <span className="hidden sm:inline text-slate-400 font-normal">· Kiel Americas Management</span>
        </button>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: brand pitch */}
          <div className="hidden lg:block text-left">
            <p className="text-cyan-400 text-xs font-bold tracking-[0.3em] uppercase mb-4">Genuine Parts · Direct from the source</p>
            <h1 className="text-4xl xl:text-5xl font-black text-white leading-tight mb-5">
              Kiel Genuine<br />Spare Parts Portal
            </h1>
            <p className="text-slate-400 text-sm leading-relaxed max-w-md mb-8">
              Identify the exact seat components you need, build a request list with genuine
              Kiel part numbers, and send it directly to Kiel Americas.
            </p>
            <div className="flex items-center gap-6 text-slate-500 text-xs">
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-cyan-500" /> Secure customer access</span>
              <span className="flex items-center gap-2"><Wrench className="w-4 h-4 text-cyan-500" /> OEM part numbers</span>
            </div>
          </div>

          {/* Right: login card */}
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white/[0.06] border border-white/10 rounded-2xl backdrop-blur-xl p-8 shadow-2xl">
              <div className="mb-6">
                <h2 className="text-xl font-bold text-white">{isAdmin ? 'Portal Administration' : 'Customer Sign In'}</h2>
                <p className="text-slate-400 text-xs mt-1">
                  {isAdmin
                    ? 'Restricted — manage spare parts customer accounts.'
                    : 'Sign in to access the Kiel genuine spare parts catalog.'}
                </p>
              </div>

              {error && (
                <div className="mb-4 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5" data-testid="portal-login-error">
                  {error}
                </div>
              )}

              <form onSubmit={isAdmin ? submitAdmin : submitCustomer} className="space-y-4">
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Email</label>
                  <div className="relative mt-1">
                    <Mail className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                      placeholder={isAdmin ? 'admin email' : 'you@company.com'}
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40"
                      data-testid="portal-email-input" />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Password</label>
                  <div className="relative mt-1">
                    <Lock className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input type={showPw ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/60 focus:ring-1 focus:ring-cyan-400/40"
                      data-testid="portal-password-input" />
                    <button type="button" onClick={() => setShowPw(s => !s)} tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading}
                  className={`w-full py-2.5 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 ${isAdmin ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' : 'bg-cyan-500 hover:bg-cyan-400 text-slate-900'}`}
                  data-testid="portal-submit-btn">
                  {loading ? 'Signing in…' : (isAdmin ? 'Sign in as Administrator' : 'Sign in to Portal')}
                </button>
              </form>

              <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-[10px] text-slate-500">
                  {isAdmin ? 'Administration area' : 'Need an account? Contact Kiel Americas.'}
                </span>
                <button onClick={() => { setMode(isAdmin ? 'customer' : 'admin'); setError(''); setPassword(''); }}
                  className="text-[10px] font-semibold text-slate-400 hover:text-cyan-300 underline underline-offset-2"
                  data-testid="portal-mode-toggle">
                  {isAdmin ? '← Customer Sign In' : 'Portal Administration'}
                </button>
              </div>
            </div>
            <p className="text-center text-[10px] text-slate-600 mt-4">© {new Date().getFullYear()} Kiel Americas · office@kielna.com</p>
          </div>
        </div>
      </main>
    </div>
  );
}
